'use strict'

const GAP = 4
const MAX_PUSHES = 5

function refreshCommentHeader (container, pill) {
  const line = Number(pill.getAttribute('data-comment-line'))
  const end = Number(pill.getAttribute('data-comment-endline'))
  const alias = pill.getAttribute('data-comment-alias') || ''
  let header = container.querySelector('.critic-comment-header')
  if (!Number.isSafeInteger(line) || line < 1 || !/^cl[1-9]\d*(?:-(?:[2-9]|[1-9]\d+))?$/.test(alias)) {
    if (header) header.remove()
    return
  }
  if (!header) {
    header = container.ownerDocument.createElement('span')
    header.className = 'critic-comment-header'
    const link = container.ownerDocument.createElement('a')
    link.className = 'critic-comment-line'
    link.addEventListener('click', event => event.stopPropagation())
    header.appendChild(link)
    container.insertBefore(header, container.firstChild)
  }
  const link = header.firstChild
  link.setAttribute('href', '#' + alias)
  link.textContent = 'Line ' + line + (Number.isSafeInteger(end) && end > line ? '–' + end : '')
}

function captureReplyState (container) {
  const input = container.querySelector('.critic-reply-input')
  if (!input) return null
  return {
    value: input.value,
    start: input.selectionStart,
    end: input.selectionEnd,
    direction: input.selectionDirection,
    top: input.scrollTop,
    left: input.scrollLeft,
    open: container.classList.contains('critic-comment-popover') && !!container.parentElement && container.parentElement.classList.contains('active'),
    focused: input.ownerDocument.activeElement === input
  }
}

function restoreReplyState (container, state, grow) {
  const input = container.querySelector('.critic-reply-input')
  if (!input || !state) return
  if (state.open && container.classList.contains('critic-comment-popover') && container.parentElement) container.parentElement.classList.add('active')
  input.value = state.value
  if (grow) grow(input)
  if (state.focused && input.isConnected) input.focus({ preventScroll: true })
  input.setSelectionRange(state.start, state.end, state.direction)
  input.scrollTop = state.top
  input.scrollLeft = state.left
}

function createCriticMargin ({ doc, makeBubble, grow }) {
  const win = doc.ownerDocument.defaultView
  const margin = doc.ownerDocument.createElement('div')
  margin.className = 'critic-margin'
  let records = []
  let groups = []
  let snapshot = null
  let frame = null
  let hidden = false
  const sizes = new WeakMap()
  const observer = win.ResizeObserver && new win.ResizeObserver(entries => {
    let changed = false
    for (const entry of entries) {
      const size = entry.contentRect.width + ':' + entry.contentRect.height
      if (sizes.get(entry.target) !== size) {
        sizes.set(entry.target, size)
        changed = true
      }
    }
    if (changed) schedule()
  })

  function capture () {
    if (snapshot) return
    snapshot = new Map(records.map(record => [record.id, captureReplyState(record.bubble)]))
  }

  function metadata () {
    const newlyActive = new Set()
    for (const record of records) {
      const active = record.pill.classList.contains('active')
      if (active && !record.active) newlyActive.add(record)
      record.active = active
      refreshCommentHeader(record.bubble, record.pill)
      for (const name of ['active', 'critic-cursor-active']) {
        record.bubble.classList.toggle(name, record.pill.classList.contains(name))
      }
    }
    for (const group of groups) {
      group.control.classList.toggle('critic-cursor-active', group.records.some(record => record.pill.classList.contains('critic-cursor-active')))
      if (group.records.some(record => newlyActive.has(record))) group.expanded = true
      showGroup(group)
    }
  }

  function showGroup (group) {
    group.control.classList.toggle('expanded', group.expanded)
    group.control.setAttribute('aria-expanded', String(group.expanded))
    group.control.textContent = group.expanded ? 'Hide comments' : '+' + group.records.length + ' more comment' + (group.records.length === 1 ? '' : 's')
    group.overflow.style.display = group.expanded ? 'block' : 'none'
  }

  function height (element) {
    return element.getBoundingClientRect().height
  }

  function natural (record, docTop) {
    return record.pill.getBoundingClientRect().top - docTop
  }

  function layout () {
    if (hidden || !margin.isConnected) return
    const docTop = doc.getBoundingClientRect().top
    let bottom = -GAP
    for (const record of records) {
      const group = record.group
      if (group) {
        if (group.records[0] !== record) continue
        const top = Math.max(natural(record, docTop), bottom + GAP)
        group.control.style.top = top + 'px'
        bottom = top + height(group.control)
        if (group.expanded) {
          for (const member of group.records) {
            const memberTop = Math.max(natural(member, docTop), bottom + GAP)
            member.bubble.style.top = memberTop + 'px'
            bottom = memberTop + height(member.bubble)
          }
        }
      } else {
        const top = Math.max(natural(record, docTop), bottom + GAP)
        record.bubble.style.top = top + 'px'
        bottom = top + height(record.bubble)
      }
    }
    margin.style.height = Math.max(0, bottom) + 'px'
  }

  function schedule () {
    if (hidden || frame !== null) return
    frame = win.requestAnimationFrame(() => {
      frame = null
      layout()
    })
  }

  function addGroup (members, expandedIds, states) {
    const control = doc.ownerDocument.createElement('div')
    control.className = 'critic-margin-more'
    control.setAttribute('role', 'button')
    control.tabIndex = 0
    const overflow = doc.ownerDocument.createElement('div')
    overflow.className = 'critic-margin-overflow'
    const group = { control, overflow, records: members, expanded: members.some(record => expandedIds.has(record.id) || (states.get(record.id) || {}).focused) }
    margin.insertBefore(control, members[0].bubble)
    margin.insertBefore(overflow, members[0].bubble)
    for (const record of members) {
      record.group = group
      overflow.appendChild(record.bubble)
    }
    const toggle = event => {
      event.preventDefault()
      event.stopPropagation()
      group.expanded = !group.expanded
      showGroup(group)
      schedule()
    }
    control.addEventListener('click', toggle)
    control.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') toggle(event)
    })
    groups.push(group)
    showGroup(group)
  }

  function partition (expandedIds, states) {
    const docTop = doc.getBoundingClientRect().top
    let bottom = -GAP
    let pushes = 0
    let members = []
    const flush = () => {
      if (members.length) addGroup(members, expandedIds, states)
      members = []
    }
    for (const record of records) {
      const y = natural(record, docTop)
      if (y < bottom + GAP) pushes++
      else {
        flush()
        pushes = 0
      }
      const top = Math.max(y, bottom + GAP)
      bottom = top + height(record.bubble)
      if (pushes > MAX_PUSHES) members.push(record)
    }
    flush()
  }

  function observe () {
    if (!observer) return
    observer.disconnect()
    for (const record of records) observer.observe(record.bubble)
    for (const group of groups) observer.observe(group.control)
  }

  function update (pills) {
    hidden = false
    if (!margin.isConnected) doc.appendChild(margin)
    const unchanged = pills.length === records.length && pills.every((pill, index) => {
      const record = records[index]
      return record.pill === pill && record.id === pill.id && record.raw === pill.getAttribute('data-critic-raw') && record.thread === pill.getAttribute('data-thread')
    })
    if (unchanged) {
      snapshot = null
      metadata()
      observe()
      schedule()
      return
    }
    const states = snapshot || new Map(records.map(record => [record.id, captureReplyState(record.bubble)]))
    snapshot = null
    const expandedIds = new Set(groups.filter(group => group.expanded).flatMap(group => group.records.map(record => record.id)))
    const previousActive = new Map(records.map(record => [record.id, record.active]))
    if (observer) observer.disconnect()
    margin.replaceChildren()
    groups = []
    records = pills.map(pill => {
      const bubble = makeBubble(pill)
      const id = pill.id
      bubble.setAttribute('data-comment-id', id)
      margin.appendChild(bubble)
      const record = { id, pill, bubble, raw: pill.getAttribute('data-critic-raw'), thread: pill.getAttribute('data-thread'), group: null, active: previousActive.get(id) }
      const state = states.get(id)
      if (state) restoreReplyState(bubble, Object.assign({}, state, { focused: false }), grow)
      return record
    })
    partition(expandedIds, states)
    metadata()
    layout()
    for (const record of records) {
      const state = states.get(record.id)
      if (state) restoreReplyState(record.bubble, state)
    }
    observe()
  }

  function hide () {
    if (hidden) return
    capture()
    hidden = true
    if (frame !== null) win.cancelAnimationFrame(frame)
    frame = null
    if (observer) observer.disconnect()
    margin.remove()
  }

  return { capture, update, schedule, layout, metadata, hide, element: margin }
}

module.exports = { createCriticMargin, refreshCommentHeader, captureReplyState, restoreReplyState }
